import ogSolutions from "@/assets/og-sections/solutions.jpg";
import ogIndustries from "@/assets/og-sections/industries.jpg";
import ogAskArif from "@/assets/og-sections/ask-arif.jpg";
import ogContact from "@/assets/og-sections/contact.jpg";
import solSecurity from "@/assets/sol-security.jpg";
import solPerimeter from "@/assets/sol-perimeter.jpg";
import solAutomation from "@/assets/sol-automation.jpg";
import solCinema from "@/assets/sol-cinema.jpg";
import solAuditorium from "@/assets/sol-auditorium.jpg";
import solEnterprise from "@/assets/sol-enterprise.jpg";
import blueprint from "@/assets/blueprint.jpg";
import portraitProcess from "@/assets/portrait-process.webp";
import portraitThinking from "@/assets/portrait-thinking.webp";
import { withCacheBust } from "@/lib/deploy-revision";

export const SITE_ORIGIN = "https://arifkhanglobal.com";

/**
 * Absolute, cache-busted social image URL.
 *
 * Vite already content-hashes the filename, but Facebook / LinkedIn / X keep
 * their own long-lived scrape caches keyed on the exact URL string. Appending
 * the deploy revision guarantees a fresh URL after every deploy, so the
 * crawlers re-fetch the creative instead of replaying a stale preview.
 */
export function ogUrl(relativeAssetPath: string): string {
  return withCacheBust(`${SITE_ORIGIN}${relativeAssetPath}`);
}

export const OG_IMAGE_WIDTH = 1216;
export const OG_IMAGE_HEIGHT = 640;

/** Section-level social preview creatives (absolute URLs, ready for og:image). */
export const SECTION_OG = {
  solutions: ogUrl(ogSolutions),
  industries: ogUrl(ogIndustries),
  askArif: ogUrl(ogAskArif),
  contact: ogUrl(ogContact),
} as const;

/**
 * Build the full set of og/twitter image tags for a section preview so every
 * hub shares a consistent, correctly-sized card.
 */
export function sectionImageMeta(image: string, alt: string) {
  return [
    { property: "og:image", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:width", content: String(OG_IMAGE_WIDTH) },
    { property: "og:image:height", content: String(OG_IMAGE_HEIGHT) },
    { property: "og:image:alt", content: alt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: alt },
  ];
}

/**
 * Per-category creatives for Ask Arif hub/category pages so each category card
 * shows a topic-relevant preview instead of one generic image.
 */
export const CATEGORY_OG: Record<string, string> = {
  "security-infrastructure": solSecurity,
  "perimeter-protection": solPerimeter,
  "smart-automation": solAutomation,
  "luxury-home-cinema": solCinema,
  "audio-video": solAuditorium,
  "intelligent-buildings": solEnterprise,
  "technology-trends": blueprint,
  "project-learnings": portraitProcess,
  "cost-guides": blueprint,
  "mistakes-to-avoid": portraitThinking,
};

/** Absolute category creative, falling back to the Ask Arif section card. */
export function categoryOgImage(categorySlug: string): string {
  const rel = CATEGORY_OG[categorySlug];
  return rel ? ogUrl(rel) : SECTION_OG.askArif;
}
